import React from "react";
import * as Styled from "./styles";
import { useClinicalBudget } from "shared/providers/ClinicalBudget";
import { useAuth } from "shared/providers/auth";
import { validarSenha } from "requests/ClinicalBudget";
import { IoCalculator, IoSettings } from "react-icons/io5";

const BudgetSummary: React.FC = () => {
  const { user } = useAuth();
  const {
    subtotal,
    total,
    desconto,
    setDesconto,
    descontoAutorizado,
    setDescontoAutorizado,
    obsDesconto,
    setObsDesconto,
    entrada,
    setEntrada,
    parcelas,
    setParcelas,
    dataVencimento,
    setDataVencimento,
    formatMoeda,
    parseMoeda,
    showValues,
  } = useClinicalBudget();

  const [showPasswordModal, setShowPasswordModal] = React.useState(false);
  const [senha, setSenha] = React.useState('');
  const [senhaError, setSenhaError] = React.useState('');
  const [validating, setValidating] = React.useState(false);

  // Formatar valor como moeda ao digitar
  const handleDescontoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    // Remove tudo exceto números
    value = value.replace(/\D/g, '');
    // Converte para número decimal
    const numValue = parseInt(value, 10) / 100;
    // Formata como moeda
    const formatted = `R$ ${formatMoeda(numValue || 0)}`;
    setDesconto(formatted);
  };

  const handleEntradaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    value = value.replace(/\D/g, '');
    const numValue = parseInt(value, 10) / 100;
    const formatted = `R$ ${formatMoeda(numValue || 0)}`;
    setEntrada(formatted);
  };

  // Abrir modal de senha
  const handleAuthorize = () => {
    setShowPasswordModal(true);
    setSenha('');
    setSenhaError('');
  };

  // Validar senha
  const handleValidateSenha = async () => {
    if (!senha) {
      setSenhaError('Digite a senha!');
      return;
    }

    setValidating(true);
    setSenhaError('');

    try {
      const userId = user?.data?.id || "";
      const response = await validarSenha(userId, senha);

      if (response.data?.autorizado) {
        setDescontoAutorizado(true);
        setShowPasswordModal(false);
      } else {
        setSenhaError('Senha incorreta!');
      }
    } catch (error) {
      setSenhaError('Erro ao validar senha');
    } finally {
      setValidating(false);
    }
  };

  const descontoParsed = parseMoeda(desconto);

  return (
    <>
      {/* Card de Resumo */}
      <Styled.SummaryCard>
        <Styled.SummaryHeader>
          <IoCalculator size={20} color="white" />
          <h4>Resumo</h4>
        </Styled.SummaryHeader>
        <Styled.SummaryBody>
          <Styled.SummaryRow>
            <Styled.SummaryLabel>Sub-total</Styled.SummaryLabel>
            <Styled.SummaryValue showValue={showValues}>
              {showValues ? `R$ ${formatMoeda(subtotal)}` : '***'}
            </Styled.SummaryValue>
          </Styled.SummaryRow>

          <Styled.SummaryRow>
            <Styled.SummaryLabel>Desconto</Styled.SummaryLabel>
            <Styled.DescontoRow>
              <Styled.DescontoInput
                type="text"
                value={desconto}
                onChange={handleDescontoChange}
                disabled={!descontoAutorizado}
              />
              {!descontoAutorizado && (
                <Styled.AuthorizeButton onClick={handleAuthorize}>
                  Autorizar
                </Styled.AuthorizeButton>
              )}
            </Styled.DescontoRow>
          </Styled.SummaryRow>

          {descontoAutorizado && descontoParsed > 0 && (
            <Styled.DescontoArea>
              <Styled.DescontoLabel>Motivo do Desconto *</Styled.DescontoLabel>
              <Styled.DescontoTextarea
                value={obsDesconto}
                onChange={(e) => setObsDesconto(e.target.value)}
                placeholder="Descreva o motivo do desconto..."
                maxLength={200}
              />
            </Styled.DescontoArea>
          )}

          <Styled.SummaryRow isTotal>
            <Styled.SummaryLabel isTotal>TOTAL</Styled.SummaryLabel>
            <Styled.SummaryValue isTotal showValue={showValues}>
              {showValues ? `R$ ${formatMoeda(total)}` : '***'}
            </Styled.SummaryValue>
          </Styled.SummaryRow>
        </Styled.SummaryBody>
      </Styled.SummaryCard>

      {/* Card de Configurações */}
      <Styled.ConfigCard>
        <Styled.ConfigHeader>
          <IoSettings size={20} color="white" />
          <h4>Configurações</h4>
        </Styled.ConfigHeader>
        <Styled.ConfigBody>
          <Styled.ConfigGroup>
            <Styled.ConfigLabel>Valor de Entrada</Styled.ConfigLabel>
            <Styled.ConfigInput
              type="text"
              value={entrada}
              onChange={handleEntradaChange}
            />
          </Styled.ConfigGroup>

          <Styled.ConfigGroup>
            <Styled.ConfigLabel>Nº de Parcelas</Styled.ConfigLabel>
            <Styled.ConfigInput
              type="number"
              value={parcelas}
              onChange={(e) => setParcelas(Math.max(1, parseInt(e.target.value) || 1))}
              min={1}
            />
          </Styled.ConfigGroup>

          <Styled.ConfigGroup>
            <Styled.ConfigLabel>1º Vencimento</Styled.ConfigLabel>
            <Styled.ConfigInput
              type="text"
              value={dataVencimento}
              onChange={(e) => setDataVencimento(e.target.value)}
              placeholder="DD/MM/AAAA"
            />
          </Styled.ConfigGroup>
        </Styled.ConfigBody>
      </Styled.ConfigCard>

      {/* Modal de Senha */}
      {showPasswordModal && (
        <Styled.PasswordModal onClick={() => setShowPasswordModal(false)}>
          <Styled.PasswordContent onClick={e => e.stopPropagation()}>
            <Styled.PasswordTitle>🔐 Autorização de Desconto</Styled.PasswordTitle>
            
            {senhaError && (
              <Styled.ErrorMessage>{senhaError}</Styled.ErrorMessage>
            )}
            
            <Styled.PasswordInput
              type="password"
              placeholder="Digite a senha"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleValidateSenha()}
              autoFocus
            />
            
            <Styled.PasswordButtons>
              <Styled.PasswordButton
                variant="secondary"
                onClick={() => setShowPasswordModal(false)}
              >
                Cancelar
              </Styled.PasswordButton>
              <Styled.PasswordButton
                variant="primary"
                onClick={handleValidateSenha}
                disabled={validating}
              >
                {validating ? 'Validando...' : 'Autorizar'}
              </Styled.PasswordButton>
            </Styled.PasswordButtons>
          </Styled.PasswordContent>
        </Styled.PasswordModal>
      )}
    </>
  );
};

export { BudgetSummary };

